import { useNavigate, Outlet } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Plus, Zap, Menu, X, Bell } from "lucide-react";
import { Button } from "./ui/button";
import AddLeadModal from "./AddLeadModal";
import ImportCsvModal from "./ImportCsvModal";
import { DesktopNav, AccountMenu, MobileNav } from "./NavMenus";
import { EmailSettingsDialog } from "./EmailSettingsDialog";

function InboxBell() {
  const navigate = useNavigate();
  const prev = useRef(0);
  const { data } = useQuery({
    queryKey: ["unread-count"],
    queryFn: () => api.get("/inbox/unread-count").then((r) => r.data),
    refetchInterval: 20000,
  });
  const count = data?.count || 0;
  useEffect(() => {
    if (count > prev.current && prev.current !== 0) {
      toast.message("📩 New lead reply", { description: "A lead just texted back — check your inbox." });
    }
    prev.current = count;
  }, [count]);
  return (
    <button className="relative p-2 rounded-lg hover:bg-secondary" onClick={() => navigate("/inbox")} data-testid="inbox-bell">
      <Bell className="h-4 w-4" />
      {count > 0 && (
        <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center" data-testid="unread-badge">
          {count}
        </span>
      )}
    </button>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 glass border-b border-border" data-testid="app-header">
        <div className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground brand-glow">
                <Zap className="h-5 w-5" />
              </div>
              <span className="font-display text-xl font-extrabold tracking-tight">LeadFlow</span>
            </div>

            <DesktopNav isAdmin={user?.role === "admin"} />

            <div className="flex items-center gap-1.5 xl:gap-2">
              <Button
                size="sm"
                onClick={() => setAddOpen(true)}
                data-testid="new-lead-button"
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Plus className="h-4 w-4 xl:mr-1.5" /> <span className="hidden xl:inline">New Lead</span>
              </Button>
              <InboxBell />
              <AccountMenu user={user} theme={theme} onToggleTheme={toggle} onEmailSettings={() => setEmailOpen(true)}
                onImport={() => setImportOpen(true)} onLogout={handleLogout} />
              <Button
                size="icon"
                variant="ghost"
                className="lg:hidden"
                onClick={() => setMobileNav((v) => !v)}
                data-testid="mobile-nav-toggle"
              >
                {mobileNav ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>

          {mobileNav && (
            <MobileNav isAdmin={user?.role === "admin"} onClose={() => setMobileNav(false)} theme={theme}
              onToggleTheme={toggle} onImport={() => setImportOpen(true)} onEmailSettings={() => setEmailOpen(true)}
              onLogout={handleLogout} />
          )}
        </div>
      </header>

      <main className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        <Outlet />
      </main>

      <AddLeadModal open={addOpen} onOpenChange={setAddOpen} />
      <ImportCsvModal open={importOpen} onOpenChange={setImportOpen} />
      <EmailSettingsDialog open={emailOpen} onOpenChange={setEmailOpen} />
    </div>
  );
}
